import {observable, action, transaction, computed} from "mobx";
import accountsAPI from "modules/accounts/api/accountsAPI";
import {authService, historyReportsSocketService, socketService} from "common/services";
import IUser from "common/interfaces/IUser";
import {
	cloudStore,
	currentUserStore,
	dashboardsStore,
	editChartsManagerStore,
	metaDataStore,
	reportingStore
} from "common/stores";
import INetwork from "common/interfaces/INetwork";
import IAccount from "modules/accounts/interfaces/IAccount";
import * as _ from "lodash";
import {UserNotificationsSocketService} from "@vidazoo/ui-framework";
import EditChartsManagerStore from "../../modules/chart/stores/EditChartsManagerStore";
import moment from "moment";
import {jwtDecode} from "jwt-decode";
import {User} from "@auth0/auth0-spa-js";

const SELECTED_ACCOUNTS_KEY = "bi_dashboards_selected_accounts";

export default class SessionStore {
	@observable public isAuthenticated: boolean;
	@observable public isAuthenticating: boolean;
	@observable public isAuthError: boolean;
	@observable public accessToken: string;
	@observable public selectedAccounts: string[];
	@observable public historyReportsIsOffline: boolean;
	@observable public userScopes: string[];
	public accounts: IAccount[];
	public indexAccounts: any;

	constructor() {
		this.isAuthenticated = false;
		this.isAuthenticating = true;
		this.isAuthError = false;
		this.accessToken = null;
		this.accounts = [];
		this.selectedAccounts = this.getStoredSelectedAccounts();
		this.indexAccounts = {};
	}

	@action public setSelectedAccounts = (accounts: string[], reload?: boolean) => {
		this.setStoredSelectedAccounts(accounts);
		this.selectedAccounts = accounts;
		currentUserStore.setNetwork(this.indexAccounts[accounts[0]].network);

		if (reload) {
			window.location.reload();
		}
	};

	private getStoredSelectedAccounts(): string[] {
		try {
			const raw = localStorage.getItem(SELECTED_ACCOUNTS_KEY);
			return raw ? JSON.parse(raw) : [];
		} catch (e) {
			return [];
		}
	}

	private setStoredSelectedAccounts(accounts: string[]) {
		localStorage.setItem(SELECTED_ACCOUNTS_KEY, JSON.stringify(accounts));
	}

	@computed
	public get userAccounts(): IAccount[] {
		return this.accounts;
	}

	@computed
	public get isMultiAccounts(): boolean {
		return this.accounts.length > 1;
	}

	@computed
	public get isMultiSelectedAccounts(): boolean {
		return this.selectedAccounts.length > 1;
	}

	public isUserAllowed(scope: string): boolean {
		if (!scope) {
			return true;
		}
		if (!this.userScopes) {
			return false;
		}
		return this.userScopes.includes(scope);
	}

	@action
	public onAuthenticated = async (accessToken: string, user?: any) => {
		this.accessToken = accessToken;
		this.isAuthError = false;
		const decodedToken = jwtDecode<{permissions: string[]}>(accessToken);
		this.userScopes = decodedToken.permissions;

		try {
			const account = await accountsAPI.me(10 * 1000);
			metaDataStore.setMetaDataByVertical(account.data.metaDataByVertical);
			metaDataStore.setAccountsByVertical(account.data.accountsByVertical);
			metaDataStore.setUsers(account.data.users);

			await this.onLogin(accessToken, account.data, user);
		} catch (err) {
			this.onLoginFailed(err);
		}
	};

	@action public logout = () => {
		authService.logout();
	};

	@action
	private async onLogin(accessToken: string, data: {
		auth: IUser,
		network: INetwork,
		accounts: { results: IAccount[] }
	},                    user?: User) {

		// const key = "stayOldByDay";
		//
		// const paramDay = `old${moment().format("DDMM")}=true`;
		// if (window.location.href.includes(paramDay)) {
		// 	this.setItemWithExpiry(key, "true", moment().add(1, "day").valueOf());
		//
		// } else {
		// 	const stayOld = this.getItemWithExpiry(key);
		// 	if (!stayOld || stayOld !== "true") {
		// 		window.location.href = "https://bd.vidazoo.com";
		// 	}
		// }

		this.accounts = data.accounts.results;
		this.indexAccounts = _.keyBy(this.accounts, "_id");
		this.selectedAccounts = this.getStoredSelectedAccounts();

		if (!this.selectedAccounts.length && this.accounts.length) {
			this.setStoredSelectedAccounts([this.accounts[0]._id]);
			this.selectedAccounts = this.getStoredSelectedAccounts();
		}

		currentUserStore.initialize({...data.auth, network: this.indexAccounts[this.selectedAccounts[0]].network}, user);
		cloudStore.initialize();
		metaDataStore.initTimePreset();

		await socketService.initialize(window.__BASE_API_URL__, this.accessToken);
		try {
			await historyReportsSocketService.initialize(window.__REPORTING_HISTORY_URL__, this.accessToken);
		} catch (err) {
			this.historyReportsSocketFailed();
		}

		try {
			await UserNotificationsSocketService.initialize((window.__USER_NOTIFICATIONS_API_URL__), this.accessToken);

		} catch (err) {
			// Notifications are not required to use the app, and the login
			// continues below either way.
			console.error("failed to initialize user notifications socket", err);
		}

		this.onSocketConnected();
		reportingStore.initAfterAllInitialize();
	}

	@action
	private historyReportsSocketFailed() {
		this.historyReportsIsOffline = true;
	}

	@action
	private onSocketConnected() {
		transaction(() => {
			this.isAuthenticated = true;
			this.isAuthenticating = false;
		});
	}

	@action
	public onLoginFailed(err) {
		const status = err && err.response && err.response.status;

		transaction(() => {
			this.accessToken = null;
			this.isAuthenticated = false;
			this.isAuthenticating = false;
			this.isAuthError = true;
		});

		// The server rejected the token, so there is no session to work with.
		// Sign the user out instead of leaving them in the app. This is guarded
		// against looping, and the screen above shows if the sign out is skipped.
		if (status === 401) {
			authService.forceLogout();
		}
	}
}
